import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import { useParams } from "react-router-dom";

import { CircularProgress, Stack, Typography } from "@mui/material";

import { useGetApprovalDataChangeSetQuery } from "services/streamingApi/approval.api";
import { DefaultPageLayout } from "features/core/DefaultPageLayout";
import { coreActions } from "features/core/store/slice";
import { ApprovalDataChangeSetEntity } from "./ApprovalDataChangeSetEntity";
import { GlobalErrorMessage } from "features/core/GlobalErrorMessage";

export interface ApprovalDataChangeSetViewerProps {
    drawerWidth: number;
}

export function ApprovalDataChangeSetViewer(
    props: ApprovalDataChangeSetViewerProps,
) {
    // hide sidebar by default
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            coreActions.setShowSidebar({
                show: false,
            }),
        );
    }, []);

    // get approval data change set id from route
    const { approvalDataChangeSetId } = useParams();

    const { data, isFetching, isSuccess, isError, error } =
        useGetApprovalDataChangeSetQuery(approvalDataChangeSetId ?? "", {
            skip: !approvalDataChangeSetId,
        });

    // get distinct entities
    const entities = useMemo(() => {
        if (!data) return;

        return [...new Set(data.changes.map((x) => x.entity))];
    }, [data]);

    return (
        <>
            <DefaultPageLayout
                drawerWidth={props.drawerWidth}
                main={
                    <Stack spacing={3}>
                        {isFetching && (
                            <CircularProgress title="Loading data change set..." />
                        )}

                        {isSuccess && data && (
                            <>
                                <Typography variant="h4">
                                    {data.title}
                                </Typography>
                                <Typography variant="body1">
                                    {data.description}
                                </Typography>
                                <Stack spacing={5}>
                                    {entities &&
                                        entities.map((entity) => (
                                            <ApprovalDataChangeSetEntity
                                                key={entity}
                                                entity={entity}
                                                changes={data.changes}
                                            />
                                        ))}
                                </Stack>
                            </>
                        )}

                        {isSuccess && !data && (
                            <GlobalErrorMessage
                                title={"Data change set not found"}
                                message={"Please contact the service desk."}
                                alertSeverity={"error"}
                            />
                        )}

                        {isError && (
                            <GlobalErrorMessage
                                title={"Unable to fetch approval information"}
                                message={"Please contact the service desk."}
                                debugData={error}
                                alertSeverity={"error"}
                            />
                        )}
                    </Stack>
                }
            />
        </>
    );
}
